import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
  Suspense,
} from "react";
import axios from "axios";
import {
  FaHome,
  FaCalendarAlt,
  FaSync,
  FaSearch,
  FaTimes,
  FaTable,
  FaPlay,
  FaPause,
  FaEdit,
  FaCheckCircle,
  FaTimesCircle,
  FaInfoCircle,
  FaRupeeSign,
  FaSlidersH,
  FaExchangeAlt,
} from "react-icons/fa";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";

/* Modal is only mounted on demand — lazy-load. */
const MdlSelectSession = React.lazy(() =>
  import("../dialog/MdlSelectSession")
);

const fontStack =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Page-local design tokens (mirrors PayRoll family). */
const ui = {
  pageBg: "#f5f7fb",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "rgba(148, 163, 184, 0.32)",
  divider: "rgba(148, 163, 184, 0.22)",
  muted: "#64748b",
  text: "#0f172a",
  textSoft: "#334155",
  ring: "rgba(3, 169, 244, 0.18)",
  danger: "#ef4444",
  success: "#10b981",
  warn: "#f59e0b",
  info: "#0ea5e9",
  radiusXl: 20,
  radiusLg: 16,
  radiusMd: 12,
  shadowSoft:
    "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
  shadowCard:
    "0 1px 2px rgba(15, 23, 42, 0.04), 0 6px 24px rgba(15, 23, 42, 0.05)",
  shadowLift:
    "0 1px 2px rgba(15, 23, 42, 0.05), 0 16px 48px rgba(15, 23, 42, 0.10)",
  heroGrad: (c) =>
    `linear-gradient(120deg, ${c.primary_color_shade9} 0%, ${c.primary_color_shade7} 45%, ${c.primary_color_shade5} 100%)`,
};

const STATUS_PALETTE = {
  active:   { bg: "rgba(16, 185, 129, 0.14)", color: "#047857", dot: "#10b981" },
  inactive: { bg: "rgba(239, 68, 68, 0.14)",  color: "#b91c1c", dot: "#ef4444" },
};

function statusPalette(isActive) {
  return isActive ? STATUS_PALETTE.active : STATUS_PALETTE.inactive;
}

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return String(value);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numeric);
}

/** Normalize a server row to consistent JS types. */
function normalizeStructure(raw) {
  return {
    ...raw,
    status:
      typeof raw.status === "string"
        ? raw.status === "1"
          ? 1
          : 0
        : raw.status ?? 0,
    fee_day_start:
      typeof raw.fee_day_start === "string"
        ? parseInt(raw.fee_day_start, 10) || 0
        : raw.fee_day_start ?? 0,
    fee_day_end:
      typeof raw.fee_day_end === "string"
        ? parseInt(raw.fee_day_end, 10) || 0
        : raw.fee_day_end ?? 0,
    day_interval:
      typeof raw.day_interval === "string"
        ? parseInt(raw.day_interval, 10) || 0
        : raw.day_interval ?? 0,
    fine_plan_value:
      typeof raw.fine_plan_value === "string"
        ? (() => {
            try {
              return JSON.parse(raw.fine_plan_value || "{}");
            } catch (_e) {
              return raw.fine_plan_value;
            }
          })()
        : raw.fine_plan_value || {},
  };
}

/** Clip a server-side date string to YYYY-MM-DD (HTML <input type="date"> format). */
function toIsoDate(value) {
  if (value == null) return "";
  const s = String(value).trim();
  if (!s) return "";
  return s.length >= 10 ? s.substring(0, 10) : s;
}

const EMPTY_SLAB_ROW = () => ({
  from_day: "1",
  to_day: "",
  calculation_type: "fixed",
  amount: "",
});

const SLAB_CALC_TYPES = [
  { value: "fixed", label: "Fixed" },
  { value: "per_day", label: "Per Day" },
  { value: "per_month", label: "Per Month" },
];

function planValueDisplay(value) {
  if (value && typeof value === "object") {
    if (value.amount !== undefined) return formatINR(value.amount);
    return "—";
  }
  if (value === null || value === undefined || value === "") return "—";
  return formatINR(value);
}

/* Scoped page-level CSS. */
const PAGE_CSS = `
.fine-page :is(.form-control, .form-select, input, textarea, select) {
  border-radius: 10px;
  border: 1px solid ${ui.border};
  background: ${ui.surface};
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${ui.text};
  transition: border-color .15s ease, box-shadow .15s ease;
}
.fine-page :is(.form-control, .form-select, input, textarea, select):focus {
  border-color: ${COLORS.primary_color_shade5};
  box-shadow: 0 0 0 4px ${ui.ring};
  outline: none;
}
.fine-page .fp-search-wrap { position: relative; display: inline-flex; align-items: center; }
.fine-page .fp-search-wrap .fp-search-icon {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  color: rgba(255,255,255,0.75); font-size: 12px; pointer-events: none;
}
.fine-page .fp-search-wrap .fp-search-clear {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  background: rgba(255,255,255,0.18); border: 0;
  width: 20px; height: 20px; border-radius: 999px;
  color: #fff; font-size: 10px;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.fine-page .fp-search {
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  border-radius: 999px;
  padding: 7px 28px 7px 30px;
  font-size: 13px;
  font-weight: 500;
  outline: none;
  min-width: 220px;
  backdrop-filter: blur(6px);
}
.fine-page .fp-search::placeholder { color: rgba(255,255,255,0.72); }
.fine-page .fp-search:focus {
  background: rgba(255,255,255,0.26);
  border-color: rgba(255,255,255,0.55);
  box-shadow: 0 0 0 4px rgba(255,255,255,0.12);
}
.fine-page .fp-icon-btn {
  width: 34px; height: 34px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.32);
  backdrop-filter: blur(6px);
  cursor: pointer;
  transition: background .15s ease;
}
.fine-page .fp-icon-btn:hover:not(:disabled) { background: rgba(255,255,255,0.28); }
.fine-page .fp-icon-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.fine-page .fp-icon-btn.spinning svg { animation: fp-spin 1s linear infinite; }
@keyframes fp-spin { 100% { transform: rotate(360deg); } }
.fine-page .fp-skeleton {
  position: relative; overflow: hidden;
  background: rgba(148, 163, 184, 0.18);
  border-radius: 8px;
}
.fine-page .fp-skeleton::after {
  content: ""; position: absolute; inset: 0; transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
  animation: fp-shimmer 1.2s infinite;
}
@keyframes fp-shimmer { 100% { transform: translateX(100%); } }
/* Escape global ".btn { background-image: none !important; box-shadow: none !important; }". */
.fine-page .fp-cta {
  background-image: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%) !important;
  background-color: ${COLORS.primary_color_shade5} !important;
  box-shadow: 0 6px 18px rgba(3, 169, 244, 0.35) !important;
  color: #fff !important;
  border: 1px solid ${COLORS.primary_color_shade7} !important;
}
.fine-page .fp-cta:hover { filter: brightness(1.04); }
.fine-page .fp-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background .15s ease;
  white-space: nowrap;
}
.fine-page .fp-pill:hover:not(:disabled) { background: rgba(255,255,255,0.28); }
.fine-page .fp-pill:disabled { opacity: 0.55; cursor: not-allowed; }
.fine-page .fp-row-action {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 11px;
  border: 0;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: transform .15s ease, filter .15s ease, box-shadow .15s ease;
}
.fine-page .fp-row-action:hover { transform: translateY(-1px); filter: brightness(1.04); }
.fine-page .fp-row-action:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }
.fine-page .lift { transition: transform .15s ease, box-shadow .15s ease; }
.fine-page .lift:hover { transform: translateY(-2px); box-shadow: ${ui.shadowLift}; }
.fine-page .fp-stat-label {
  color: ${ui.muted};
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.fine-page .fp-stat-value {
  color: ${ui.text};
  font-weight: 700;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}
.fine-page .fp-card {
  background: ${ui.surface};
  border: 1px solid ${ui.border};
  border-radius: ${ui.radiusMd}px;
  box-shadow: ${ui.shadowSoft};
  padding: 14px 16px;
}

/* Card-grid design (FineStructureCard) */
.fine-page .fp-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
@media (max-width: 767.98px) {
  .fine-page .fp-cards-grid { grid-template-columns: 1fr; }
}
.fine-page .fp-scard {
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
  border: 1px solid ${ui.border};
  border-radius: 18px;
  box-shadow: ${ui.shadowSoft};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}
.fine-page .fp-scard:hover {
  transform: translateY(-3px);
  box-shadow: ${ui.shadowLift};
  border-color: rgba(3, 169, 244, 0.45);
}
.fine-page .fp-scard--inactive {
  background: linear-gradient(180deg, #ffffff 0%, #fafafa 100%);
}
.fine-page .fp-scard--inactive:hover {
  border-color: rgba(239, 68, 68, 0.35);
}
.fine-page .fp-scard--inactive .fp-scard-icon {
  background: rgba(148, 163, 184, 0.18);
  color: ${ui.muted};
  box-shadow: none;
}
.fine-page .fp-scard-head {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 15px 16px 12px;
}
.fine-page .fp-scard-icon {
  width: 42px; height: 42px; border-radius: 13px;
  display: inline-flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 100%);
  color: #fff;
  flex-shrink: 0;
  font-size: 16px;
  box-shadow: 0 4px 12px rgba(3, 169, 244, 0.30);
}
.fine-page .fp-scard-classname {
  font-weight: 800; font-size: 15.5px; color: ${ui.text};
  line-height: 1.25;
  letter-spacing: 0.005em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fine-page .fp-scard-rule {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 4px;
  max-width: 100%;
  padding: 2px 9px;
  border-radius: 999px;
  font-size: 11px; font-weight: 700;
  color: ${COLORS.primary_color_shade8};
  background: rgba(3, 169, 244, 0.09);
  border: 1px solid rgba(3, 169, 244, 0.20);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fine-page .fp-status-chip {
  display: inline-flex; align-items: center; gap: 5px;
  font-weight: 800; font-size: 10.5px;
  padding: 5px 11px; border-radius: 999px;
  letter-spacing: 0.06em; text-transform: uppercase;
  flex-shrink: 0;
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.4);
}
.fine-page .fp-scard-head-right {
  display: flex; flex-direction: column; align-items: flex-end; gap: 5px;
  flex-shrink: 0;
}
.fine-page .fp-scard-effective {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10.5px; font-weight: 700;
  color: #475569;
  background: rgba(148, 163, 184, 0.20);
  padding: 3px 9px;
  border-radius: 999px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.fine-page .fp-scard-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 7px;
  padding: 2px 14px 14px;
}
.fine-page .fp-scard-stat {
  border-radius: 12px;
  padding: 8px 6px;
  text-align: center;
  min-width: 0;
  border: 1px solid transparent;
  transition: transform .15s ease;
}
.fine-page .fp-scard-stat:hover { transform: translateY(-1px); }
.fine-page .fp-scard-stat-label {
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.09em; text-transform: uppercase;
  opacity: 0.75;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fine-page .fp-scard-stat-value {
  font-size: 13.5px; font-weight: 800;
  font-variant-numeric: tabular-nums;
  margin-top: 3px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fine-page .fp-scard-actions {
  display: flex; align-items: center; gap: 7px;
  padding: 11px 14px;
  border-top: 1px solid ${ui.divider};
  margin-top: auto;
  background: rgba(248, 250, 252, 0.7);
}
.fine-page .fp-scard-btn {
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  background: #ffffff;
  border: 1.5px solid color-mix(in srgb, var(--btn-color) 38%, #e5e7eb);
  color: var(--btn-color);
  border-radius: 10px;
  padding: 8px 6px;
  font-size: 11.5px; font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  min-width: 0;
  transition: background .15s ease, color .15s ease, border-color .15s ease,
    transform .15s ease, box-shadow .15s ease;
}
.fine-page .fp-scard-btn svg { font-size: 11px; }
.fine-page .fp-scard-btn:hover:not(:disabled) {
  background: var(--btn-color);
  border-color: var(--btn-color);
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px color-mix(in srgb, var(--btn-color) 35%, transparent);
}
.fine-page .fp-scard-btn:active:not(:disabled) { transform: translateY(0); }
.fine-page .fp-scard-btn:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

.fine-page @keyframes fp-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
.fine-page .fp-dot-pulse { animation: fp-pulse 2s ease-in-out infinite; }
/* Dialog */
.fine-page .fp-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 2050; /* above fixed page header (1035) and sidebar (1030) */
  padding: 16px;
}
.fine-page .fp-modal {
  width: 100%;
  max-width: 600px;
  background: ${ui.surface};
  border-radius: ${ui.radiusLg}px;
  border: 1px solid ${ui.border};
  box-shadow: ${ui.shadowLift};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
}
.fine-page .fp-modal-header {
  padding: 14px 18px;
  color: #fff;
  background: linear-gradient(135deg, ${COLORS.primary_color_shade8} 0%, ${COLORS.primary_color_shade6} 100%);
  position: relative;
}
.fine-page .fp-modal-close {
  position: absolute; top: 12px; right: 12px;
  width: 28px; height: 28px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.32);
  color: #fff; cursor: pointer;
  backdrop-filter: blur(6px);
  font-size: 11px;
}
.fine-page .fp-modal-close:hover { background: rgba(255,255,255,0.28); }
.fine-page .fp-modal-body { padding: 18px; overflow: auto; }
.fine-page .fp-modal-footer {
  padding: 12px 18px;
  border-top: 1px solid ${ui.divider};
  display: flex; justify-content: flex-end; gap: 8px;
  background: ${ui.surfaceAlt};
}
.fine-page .fp-modal-label {
  font-size: 11px;
  font-weight: 800;
  color: ${ui.muted};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.fine-page .fp-btn-ghost {
  background: ${ui.surface};
  border: 1px solid ${ui.border};
  color: ${ui.textSoft};
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.fine-page .fp-btn-ghost:hover:not(:disabled) { background: ${ui.surfaceAlt}; }
.fine-page .fp-btn-ghost:disabled { opacity: 0.55; cursor: not-allowed; }

/* Compact slab editor */
.fine-page .fp-slab-grid {
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) minmax(0, 1fr) 104px minmax(0, 1fr) 24px;
  gap: 5px 6px;
  align-items: center;
}
.fine-page .fp-slab-grid .fp-slab-col-label {
  font-size: 9.5px;
  font-weight: 800;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${ui.muted};
  padding: 0 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.fine-page .fp-slab-grid :is(.form-control, .form-select) {
  padding: 5px 8px;
  font-size: 12.5px;
  border-radius: 8px;
  min-width: 0;
}
.fine-page .fp-slab-index {
  font-size: 10.5px;
  font-weight: 800;
  color: ${ui.muted};
  text-align: center;
}
.fine-page .fp-slab-remove {
  width: 24px; height: 24px;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 999px;
  border: 0;
  font-size: 10px;
  cursor: pointer;
  background: rgba(239, 68, 68, 0.12);
  color: #b91c1c;
}
.fine-page .fp-slab-remove:disabled {
  background: rgba(148, 163, 184, 0.18);
  color: ${ui.muted};
  cursor: not-allowed;
}
.fine-page .fp-slab-add {
  grid-column: 1 / -1;
  justify-self: start;
  padding: 4px 12px;
  font-size: 12px;
}
@media (max-width: 480px) {
  .fine-page .fp-slab-grid {
    grid-template-columns: 14px minmax(0, 1fr) minmax(0, 1fr) 84px minmax(0, 1fr) 22px;
    gap: 4px;
  }
  .fine-page .fp-slab-grid :is(.form-control, .form-select) {
    padding: 4px 6px;
    font-size: 11.5px;
  }
}
`;

/* Decorative orbs (constants — never re-rendered). */
const HERO_ORB_TR = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -60,
      top: -60,
      width: 220,
      height: 220,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.08)",
      filter: "blur(2px)",
    }}
  />
);
const HERO_ORB_BL = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      left: -40,
      bottom: -80,
      width: 180,
      height: 180,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.06)",
    }}
  />
);
const LIST_HEADER_ORB = (
  <span
    aria-hidden
    style={{
      position: "absolute",
      right: -40,
      top: -40,
      width: 160,
      height: 160,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.07)",
    }}
  />
);

/* ---------- Sub-components ---------- */

function DialogShell({ title, subtitle, onClose, footer, children, maxWidth }) {
  return (
    <div className="fp-modal-backdrop" role="dialog" aria-modal="true">
      <div className="fp-modal" style={maxWidth ? { maxWidth } : undefined}>
        <div className="fp-modal-header">
          {onClose ? (
            <button
              type="button"
              className="fp-modal-close"
              onClick={onClose}
              aria-label="Close"
              title="Close"
            >
              <FaTimes aria-hidden />
            </button>
          ) : null}
          <div style={{ fontWeight: 800, fontSize: 16, paddingRight: 32 }}>
            {title}
          </div>
          {subtitle ? (
            <div
              style={{
                fontSize: 12,
                opacity: 0.9,
                marginTop: 2,
                paddingRight: 32,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        <div className="fp-modal-body">{children}</div>
        {footer ? <div className="fp-modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="fp-scard">
      <div className="fp-scard-head">
        <div
          className="fp-skeleton"
          style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="fp-skeleton"
            style={{ height: 13, width: "65%", marginBottom: 6 }}
          />
          <div
            className="fp-skeleton"
            style={{ height: 10, width: "45%" }}
          />
        </div>
        <div className="fp-scard-head-right">
          <div
            className="fp-skeleton"
            style={{ height: 22, width: 64, borderRadius: 999 }}
          />
          <div
            className="fp-skeleton"
            style={{ height: 16, width: 80, borderRadius: 999 }}
          />
        </div>
      </div>
      <div className="fp-scard-stats">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="fp-skeleton"
            style={{ height: 44, borderRadius: 12 }}
          />
        ))}
      </div>
      <div className="fp-scard-actions">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="fp-skeleton"
            style={{ height: 32, flex: 1, borderRadius: 10 }}
          />
        ))}
      </div>
    </div>
  );
}

const CHIP_PALETTES = {
  feeDays: { bg: "rgba(3, 169, 244, 0.10)", color: "#0369a1" },
  plan:    { bg: "rgba(139, 92, 246, 0.14)", color: "#6d28d9" },
  grace:   { bg: "rgba(16, 185, 129, 0.12)", color: "#047857" },
  maxCap:  { bg: "rgba(245, 158, 11, 0.16)", color: "#b45309" },
  from:    { bg: "rgba(148, 163, 184, 0.20)", color: "#475569" },
};

const FineStructureCard = React.memo(function FineStructureCard({
  structure,
  onToggle,
  onEditFeeDays,
  onEditPlan,
  onChangePlan,
}) {
  const isActive = structure.status === 1;
  const palette = statusPalette(isActive);
  const className =
    (structure.class_name || structure.class_id || "Unnamed class") +
    (structure.section_name && structure.section_name !== "No Section"
      ? `: ${structure.section_name}`
      : "");

  const stats = [
    {
      label: "Fee Days",
      value: `${structure.fee_day_start ?? 0}–${structure.fee_day_end ?? 0}`,
      ...CHIP_PALETTES.feeDays,
    },
    {
      label: "Plan Value",
      value:
        structure.rule_type === "slab"
          ? "Slab based"
          : planValueDisplay(structure.fine_plan_value),
      ...CHIP_PALETTES.plan,
    },
    {
      label: "Grace",
      value: `${structure.grace_days ?? 0}d`,
      ...CHIP_PALETTES.grace,
    },
    {
      label: "Max Cap",
      value: structure.max_cap ? formatINR(structure.max_cap) : "No limit",
      ...CHIP_PALETTES.maxCap,
    },
  ];

  return (
    <div className={`fp-scard${isActive ? "" : " fp-scard--inactive"}`}>
      {/* Header */}
      <div className="fp-scard-head">
        <span className="fp-scard-icon" aria-hidden>
          <FaTable />
        </span>
        <div className="min-w-0" style={{ flex: 1 }}>
          <div className="fp-scard-classname" title={className}>
            {className}
          </div>
          <div
            className="fp-scard-rule"
            title={structure.fine_rule_name || undefined}
          >
            {structure.fine_rule_name || "—"}
            {structure.rule_type ? (
              <>
                {" · "}
                <span style={{ textTransform: "capitalize" }}>
                  {structure.rule_type}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="fp-scard-head-right">
          <span
            className="fp-status-chip"
            style={{ background: palette.bg, color: palette.color }}
          >
            <span
              aria-hidden
              className={isActive ? "fp-dot-pulse" : undefined}
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: palette.dot,
              }}
            />
            {isActive ? "Active" : "Inactive"}
          </span>
          <span
            className="fp-scard-effective"
            title={`Effective From: ${structure.effective_from || "—"}`}
          >
            <FaCalendarAlt aria-hidden style={{ fontSize: 9 }} />
            {structure.effective_from || "—"}
          </span>
        </div>
      </div>

      {/* Stats */}
      <div className="fp-scard-stats" aria-label="Fine structure stats">
        {stats.map((s) => (
          <div
            className="fp-scard-stat"
            key={s.label}
            title={`${s.label}: ${s.value}`}
            style={{ background: s.bg, color: s.color }}
          >
            <div className="fp-scard-stat-label">{s.label}</div>
            <div className="fp-scard-stat-value">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="fp-scard-actions">
        <button
          type="button"
          className="fp-scard-btn"
          onClick={() => onToggle(structure)}
          style={{ "--btn-color": isActive ? "#dc2626" : "#059669" }}
          title={isActive ? "Deactivate" : "Activate"}
        >
          {isActive ? <FaPause aria-hidden /> : <FaPlay aria-hidden />}
          {isActive ? "Pause" : "Activate"}
        </button>
        <button
          type="button"
          className="fp-scard-btn"
          onClick={() => onEditFeeDays(structure)}
          style={{ "--btn-color": "#0284c7" }}
          title="Edit fee days"
        >
          <FaCalendarAlt aria-hidden />
          Fee Days
        </button>
        <button
          type="button"
          className="fp-scard-btn"
          onClick={() => onEditPlan(structure)}
          style={{ "--btn-color": "#7c3aed" }}
          title="Edit plan, grace & max cap"
        >
          <FaEdit aria-hidden />
          Edit
        </button>
        <button
          type="button"
          className="fp-scard-btn"
          onClick={() => onChangePlan(structure)}
          style={{ "--btn-color": "#d97706" }}
          title="Change plan"
        >
          <FaExchangeAlt aria-hidden />
          Plan
        </button>
      </div>
    </div>
  );
});

/* ---------- Main component ---------- */

const FineSettings = () => {
  const [SESSION_ID, setSESSION_ID] = useState("");
  const [SESSION_NAME, setSESSION_NAME] = useState("");
  const [SESSION_START_DATE, setSESSION_START_DATE] = useState("");
  const [SESSION_END_DATE, setSESSION_END_DATE] = useState("");
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [fineStructures, setFineStructures] = useState([]);
  const [isLoadingStructures, setIsLoadingStructures] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  /* Fee-days dialog state */
  const [showEditFeeDaysDialog, setShowEditFeeDaysDialog] = useState(false);
  const [selectedStructureForFeeDays, setSelectedStructureForFeeDays] =
    useState(null);
  const [feeDayStart, setFeeDayStart] = useState("");
  const [feeDayEnd, setFeeDayEnd] = useState("");
  const [isEditingFeeDays, setIsEditingFeeDays] = useState(false);

  /* Plan-value dialog state */
  const [showEditPlanValueDialog, setShowEditPlanValueDialog] = useState(false);
  const [selectedStructureForPlanValue, setSelectedStructureForPlanValue] =
    useState(null);
  const [planValue, setPlanValue] = useState("");
  const [graceDays, setGraceDays] = useState("");
  const [maxCap, setMaxCap] = useState("");
  const [noMaxCapLimit, setNoMaxCapLimit] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState("");
  const [isEditingPlanValue, setIsEditingPlanValue] = useState(false);

  /* Slab editor state (used when the structure's rule_type is "slab") */
  const [slabRows, setSlabRows] = useState([]);
  const [isLoadingSlabs, setIsLoadingSlabs] = useState(false);

  /* Change-plan dialog state */
  const [showChangePlanDialog, setShowChangePlanDialog] = useState(false);
  const [selectedStructureForChangePlan, setSelectedStructureForChangePlan] =
    useState(null);
  const [availableFineRules, setAvailableFineRules] = useState([]);
  const [isLoadingFineRules, setIsLoadingFineRules] = useState(false);
  const [newFineRuleId, setNewFineRuleId] = useState("");
  const [isChangingPlan, setIsChangingPlan] = useState(false);

  /* Stale-request guard. */
  const fetchReqIdRef = useRef(0);

  /* Mount: scroll top + bootstrap session. */
  useEffect(() => {
    window.scrollTo(0, 0);
    const session =
      SessionManager.getCurrentSession && SessionManager.getCurrentSession();
    if (Array.isArray(session) && session[0]) {
      setSESSION_ID(session[0].session_id || "");
      setSESSION_NAME(session[0].session_name || "");
      setSESSION_START_DATE(toIsoDate(session[0].start_date));
      setSESSION_END_DATE(toIsoDate(session[0].end_date));
    } else {
      setIsOpenSessionDialog(true);
    }
    return () => {
      fetchReqIdRef.current = -1;
    };
  }, []);

  const fetchAllClassFineStructure = useCallback(
    async (silent = false, sessionIdOverride) => {
      const sid =
        sessionIdOverride !== undefined && sessionIdOverride !== null
          ? sessionIdOverride
          : SESSION_ID;
      if (!sid) {
        if (!silent) myToast.notify_warning("Please select a session first");
        return;
      }
      const reqId = ++fetchReqIdRef.current;
      setIsLoadingStructures(true);
      try {
        const FUNCTION_NAME = "getAllClassFineStructure.php";
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
        const schoolCode =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const params = { school_code: schoolCode, session_id: sid };

        const queryString = new URLSearchParams(
          Object.fromEntries(
            Object.entries(params).map(([k, v]) => [
              k,
              v != null ? String(v) : "",
            ])
          )
        ).toString();
        console.log(
          "getAllClassFineStructure full URL:",
          `${URL}?${queryString}`
        );

        const { data: response } = await axios.get(URL, { params });
        if (reqId !== fetchReqIdRef.current) return;

        if (response?.api_status === "OK" && Array.isArray(response?.data)) {
          const normalized = response.data.map(normalizeStructure);
          setFineStructures(normalized);
          if (!silent) {
            myToast.notify_success(
              `Loaded ${normalized.length} fine structure${
                normalized.length === 1 ? "" : "s"
              }`
            );
          }
        } else {
          setFineStructures([]);
          if (!silent) {
            myToast.notify_warning(
              response?.api_remark || "Failed to load fine structures"
            );
          }
        }
      } catch (e) {
        if (reqId !== fetchReqIdRef.current) return;
        setFineStructures([]);
        if (!silent) {
          myToast.notify_error(
            "Error loading fine structures: " + (e?.message || "Unknown error")
          );
        }
      } finally {
        if (reqId === fetchReqIdRef.current) setIsLoadingStructures(false);
      }
    },
    [SESSION_ID]
  );

  /* Auto-fetch when session changes. */
  useEffect(() => {
    if (SESSION_ID) fetchAllClassFineStructure(true);
    // We intentionally only depend on SESSION_ID; the fetcher closes over the
    // latest SESSION_ID via useCallback, but we don't need to re-fire just
    // because the callback identity changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [SESSION_ID]);

  const ChangeSession = useCallback(() => setIsOpenSessionDialog(true), []);
  const HandleCloseSessionDialog = useCallback(
    () => setIsOpenSessionDialog(false),
    []
  );
  const HandleSelectedSession = useCallback(
    (session_id, session_name, session_status, start_date, end_date) => {
      // Persist so the new session sticks across pages.
      try {
        SessionManager.saveCurrentSession &&
          SessionManager.saveCurrentSession([
            {
              session_id,
              session_name,
              status: session_status,
              start_date,
              end_date,
            },
          ]);
      } catch (_e) {
        /* non-fatal */
      }
      setSESSION_ID(session_id);
      setSESSION_NAME(session_name);
      setSESSION_START_DATE(toIsoDate(start_date));
      setSESSION_END_DATE(toIsoDate(end_date));
      setIsOpenSessionDialog(false);
      // Refresh data immediately with the new session id so we don't rely on
      // any effect/closure timing.
      if (session_id) fetchAllClassFineStructure(true, session_id);
    },
    [fetchAllClassFineStructure]
  );

  const toggleStructureStatus = useCallback(
    async (structure) => {
      if (!structure?.id) return;
      const newStatus = structure.status === 1 ? 0 : 1;
      const action = newStatus === 1 ? "activate" : "deactivate";
      if (
        !window.confirm(
          `${action.charAt(0).toUpperCase() + action.slice(1)} this fine structure?`
        )
      )
        return;
      try {
        const FUNCTION_NAME = "updateFineStructureStatus.php";
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
        const schoolCode =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const { data: response } = await axios.get(URL, {
          params: {
            school_code: schoolCode,
            session_id: SESSION_ID,
            fine_rule_id: structure.fine_rule_id,
            status: newStatus,
            class_id: structure.class_id,
            section_id: structure.section_id,
          },
        });
        if (response?.api_status === "OK") {
          myToast.notify_success(`Fine structure ${action}d`);
          fetchAllClassFineStructure(true);
        } else {
          myToast.notify_warning(
            response?.api_remark || `Failed to ${action} fine structure`
          );
        }
      } catch (e) {
        console.error(`Error ${action}ing fine structure:`, e);
        myToast.notify_error(`Error ${action}ing fine structure`);
      }
    },
    [SESSION_ID, fetchAllClassFineStructure]
  );

  const updateAllFineStructureStatus = useCallback(
    async (newStatus) => {
      const label = newStatus === 1 ? "activate" : "deactivate";
      if (!window.confirm(`${label[0].toUpperCase() + label.slice(1)} ALL fine structures?`))
        return;
      try {
        const FUNCTION_NAME = "updateAllFineStructureStatus.php";
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
        const schoolCode =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const { data: response } = await axios.get(URL, {
          params: {
            school_code: schoolCode,
            session_id: SESSION_ID,
            new_status: newStatus,
          },
        });
        if (response?.api_status === "OK") {
          myToast.notify_success(
            `All fine structures ${newStatus === 1 ? "activated" : "deactivated"}`
          );
          fetchAllClassFineStructure(true);
        } else {
          myToast.notify_warning(
            response?.api_remark || "Failed to update fine structures"
          );
        }
      } catch (e) {
        console.error("Error updating all fine structures:", e);
        myToast.notify_error("Error updating fine structures");
      }
    },
    [SESSION_ID, fetchAllClassFineStructure]
  );

  /* ---------- Fee days dialog ---------- */
  const openEditFeeDaysDialog = useCallback((structure) => {
    setSelectedStructureForFeeDays(structure);
    setFeeDayStart(structure.fee_day_start ?? "");
    setFeeDayEnd(structure.fee_day_end ?? "");
    setShowEditFeeDaysDialog(true);
  }, []);

  const closeEditFeeDaysDialog = useCallback(() => {
    if (isEditingFeeDays) return;
    setShowEditFeeDaysDialog(false);
    setSelectedStructureForFeeDays(null);
    setFeeDayStart("");
    setFeeDayEnd("");
  }, [isEditingFeeDays]);

  const validateFeeDays = useCallback(() => {
    const startDay = parseInt(feeDayStart, 10);
    const endDay = parseInt(feeDayEnd, 10);
    if (Number.isNaN(startDay) || Number.isNaN(endDay)) {
      myToast.notify_warning("Please enter valid fee day values");
      return null;
    }
    if (startDay < 1 || startDay > 31) {
      myToast.notify_warning("Fee day start must be between 1 and 31");
      return null;
    }
    if (endDay < 1 || endDay > 31) {
      myToast.notify_warning("Fee day end must be between 1 and 31");
      return null;
    }
    if (startDay > endDay) {
      myToast.notify_warning(
        "Fee day end cannot be earlier than fee day start"
      );
      return null;
    }
    return { startDay, endDay };
  }, [feeDayStart, feeDayEnd]);

  const updateFeeDays = useCallback(async () => {
    if (!selectedStructureForFeeDays) return;
    const v = validateFeeDays();
    if (!v) return;
    setIsEditingFeeDays(true);
    try {
      const FUNCTION_NAME = "updateFineStructureFeeDays.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
      const schoolCode =
        SessionManager.getSchoolCode && SessionManager.getSchoolCode();
      const { data: response } = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: SESSION_ID,
          fine_rule_id: selectedStructureForFeeDays.fine_rule_id,
          fee_day_start: v.startDay,
          fee_day_end: v.endDay,
          class_id: selectedStructureForFeeDays.class_id,
          section_id: selectedStructureForFeeDays.section_id,
        },
      });
      if (response?.api_status === "OK") {
        myToast.notify_success("Fee days updated");
        fetchAllClassFineStructure(true);
        setShowEditFeeDaysDialog(false);
        setSelectedStructureForFeeDays(null);
        setFeeDayStart("");
        setFeeDayEnd("");
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to update fee days"
        );
      }
    } catch (e) {
      console.error("Error updating fee days:", e);
      myToast.notify_error("Error updating fee days");
    } finally {
      setIsEditingFeeDays(false);
    }
  }, [
    selectedStructureForFeeDays,
    validateFeeDays,
    SESSION_ID,
    fetchAllClassFineStructure,
  ]);

  const updateAllFeeDays = useCallback(async () => {
    const v = validateFeeDays();
    if (!v) return;
    if (
      !window.confirm(
        `Update fee days for ALL fine structures to ${v.startDay}-${v.endDay}?`
      )
    )
      return;
    setIsEditingFeeDays(true);
    try {
      const FUNCTION_NAME = "updateAllFineStructureFeeDays.php";
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${FUNCTION_NAME}`;
      const schoolCode =
        SessionManager.getSchoolCode && SessionManager.getSchoolCode();
      const { data: response } = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: SESSION_ID,
          fee_day_start: v.startDay,
          fee_day_end: v.endDay,
        },
      });
      if (response?.api_status === "OK") {
        myToast.notify_success(
          `All fine structures fee days updated to ${v.startDay}-${v.endDay}`
        );
        fetchAllClassFineStructure(true);
        setShowEditFeeDaysDialog(false);
        setSelectedStructureForFeeDays(null);
        setFeeDayStart("");
        setFeeDayEnd("");
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to update fee days"
        );
      }
    } catch (e) {
      console.error("Error updating all fee days:", e);
      myToast.notify_error("Error updating fee days");
    } finally {
      setIsEditingFeeDays(false);
    }
  }, [validateFeeDays, SESSION_ID, fetchAllClassFineStructure]);

  /* ---------- Plan value dialog ---------- */
  const openEditPlanValueDialog = useCallback(async (structure) => {
    setSelectedStructureForPlanValue(structure);
    const v = structure.fine_plan_value;
    const numeric =
      v && typeof v === "object" ? v.amount ?? "" : v ?? "";
    setPlanValue(String(numeric ?? ""));
    setGraceDays(String(structure.grace_days ?? ""));
    // Treat null / undefined / "" / 0 / "0" as "no limit".
    const rawCap = structure.max_cap;
    const capStr = rawCap === null || rawCap === undefined ? "" : String(rawCap);
    const isNoLimit = capStr.trim() === "" || Number(capStr) === 0;
    setNoMaxCapLimit(isNoLimit);
    setMaxCap(isNoLimit ? "" : capStr);
    // Pre-fill effective_from as YYYY-MM-DD (HTML date input format).
    const eff = String(structure.effective_from ?? "");
    const yymmdd = eff.length >= 10 ? eff.substring(0, 10) : eff;
    setEffectiveFrom(yymmdd);
    setShowEditPlanValueDialog(true);

    // Slab rules: load the slab table for this rule.
    if (structure.rule_type === "slab") {
      setIsLoadingSlabs(true);
      setSlabRows([]);
      try {
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getFineRuleSlabs.php`;
        const { data: response } = await axios.get(URL, {
          params: { fine_rule_id: structure.fine_rule_id },
        });
        if (response?.api_status === "OK" && Array.isArray(response?.data)) {
          setSlabRows(
            response.data.length > 0
              ? response.data.map((s) => ({
                  from_day: String(s.from_day ?? ""),
                  to_day: s.to_day === null ? "" : String(s.to_day ?? ""),
                  calculation_type: s.calculation_type || "fixed",
                  amount: String(s.amount ?? ""),
                }))
              : [EMPTY_SLAB_ROW()]
          );
        } else {
          setSlabRows([EMPTY_SLAB_ROW()]);
        }
      } catch (e) {
        console.error("Error loading slabs:", e);
        setSlabRows([EMPTY_SLAB_ROW()]);
        myToast.notify_warning("Could not load existing slabs");
      } finally {
        setIsLoadingSlabs(false);
      }
    }
  }, []);

  /* ---------- Slab row helpers ---------- */
  const updateSlabRow = useCallback((index, field, value) => {
    setSlabRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  }, []);

  const addSlabRow = useCallback(() => {
    setSlabRows((rows) => {
      const last = rows[rows.length - 1];
      const nextFrom =
        last && String(last.to_day).trim() !== ""
          ? String((parseInt(last.to_day, 10) || 0) + 1)
          : "";
      return [...rows, { ...EMPTY_SLAB_ROW(), from_day: nextFrom }];
    });
  }, []);

  const removeSlabRow = useCallback((index) => {
    setSlabRows((rows) => rows.filter((_, i) => i !== index));
  }, []);

  const closeEditPlanValueDialog = useCallback(() => {
    if (isEditingPlanValue) return;
    setShowEditPlanValueDialog(false);
    setSelectedStructureForPlanValue(null);
    setPlanValue("");
    setGraceDays("");
    setMaxCap("");
    setNoMaxCapLimit(false);
    setEffectiveFrom("");
    setSlabRows([]);
  }, [isEditingPlanValue]);

  const isSlabRule = selectedStructureForPlanValue?.rule_type === "slab";

  const updateSlabPlan = useCallback(async () => {
    if (!selectedStructureForPlanValue) return;
    if (!graceDays.trim()) {
      myToast.notify_warning("Grace days is required");
      return;
    }
    const graceDaysValue = parseInt(graceDays, 10);
    if (Number.isNaN(graceDaysValue) || graceDaysValue < 0) {
      myToast.notify_warning("Grace days must be a non-negative number");
      return;
    }

    if (slabRows.length === 0) {
      myToast.notify_warning("Add at least one slab");
      return;
    }

    // Validate rows. to_day may be empty = open-ended (no upper limit).
    const parsed = [];
    for (let i = 0; i < slabRows.length; i++) {
      const row = slabRows[i];
      const from = parseInt(row.from_day, 10);
      const toRaw = String(row.to_day ?? "").trim();
      const to = toRaw === "" ? null : parseInt(toRaw, 10);
      const amount = parseFloat(row.amount);

      if (Number.isNaN(from) || from < 1) {
        myToast.notify_warning(`Slab ${i + 1}: "From day" must be 1 or more`);
        return;
      }
      if (to !== null && (Number.isNaN(to) || to < from)) {
        myToast.notify_warning(
          `Slab ${i + 1}: "To day" must be greater than or equal to "From day" (or left empty for no upper limit)`
        );
        return;
      }
      if (Number.isNaN(amount) || amount < 0) {
        myToast.notify_warning(
          `Slab ${i + 1}: amount must be a non-negative number`
        );
        return;
      }
      parsed.push({
        from_day: from,
        to_day: to,
        calculation_type: row.calculation_type || "fixed",
        amount,
      });
    }

    // Check overlaps (sorted by from_day). Open-ended slab must be last.
    const sorted = [...parsed].sort((a, b) => a.from_day - b.from_day);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i - 1].to_day === null) {
        myToast.notify_warning(
          "Only the last slab can have an open-ended day range (empty \"To day\")"
        );
        return;
      }
      if (sorted[i].from_day <= sorted[i - 1].to_day) {
        myToast.notify_warning(
          `Slab day ranges overlap (${sorted[i - 1].from_day}–${sorted[i - 1].to_day} and ${sorted[i].from_day}–${sorted[i].to_day ?? "∞"})`
        );
        return;
      }
    }

    // Effective From is optional. If provided, must be valid YYYY-MM-DD and
    // within the current session's date range (when known).
    const effectiveFromTrimmed = effectiveFrom.trim();
    if (effectiveFromTrimmed) {
      const isIso = /^\d{4}-\d{2}-\d{2}$/.test(effectiveFromTrimmed);
      const d = new Date(effectiveFromTrimmed);
      if (!isIso || Number.isNaN(d.getTime())) {
        myToast.notify_warning(
          "Effective From must be a valid date (YYYY-MM-DD)"
        );
        return;
      }
      if (SESSION_START_DATE && effectiveFromTrimmed < SESSION_START_DATE) {
        myToast.notify_warning(
          `Effective From cannot be before the session start (${SESSION_START_DATE})`
        );
        return;
      }
      if (SESSION_END_DATE && effectiveFromTrimmed > SESSION_END_DATE) {
        myToast.notify_warning(
          `Effective From cannot be after the session end (${SESSION_END_DATE})`
        );
        return;
      }
    }

    setIsEditingPlanValue(true);
    try {
      const schoolCode =
        SessionManager.getSchoolCode && SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateFineRuleSlabs.php`;
      const body = new URLSearchParams({
        school_code: schoolCode || "",
        session_id: SESSION_ID,
        fine_rule_id: selectedStructureForPlanValue.fine_rule_id,
        slabs: JSON.stringify(sorted),
        grace_days: String(graceDaysValue),
        effective_from: effectiveFromTrimmed,
        class_id: selectedStructureForPlanValue.class_id || "",
        section_id: selectedStructureForPlanValue.section_id || "",
      });
      const { data: response } = await axios.post(URL, body);

      if (response?.api_status === "OK") {
        myToast.notify_success(response?.api_remark || "Slab plan updated");
        fetchAllClassFineStructure(true);
        setShowEditPlanValueDialog(false);
        setSelectedStructureForPlanValue(null);
        setGraceDays("");
        setEffectiveFrom("");
        setSlabRows([]);
      } else {
        myToast.notify_warning(
          response?.api_remark || "Failed to update slab plan"
        );
      }
    } catch (e) {
      console.error("Error updating slab plan:", e);
      myToast.notify_error("Error updating slab plan");
    } finally {
      setIsEditingPlanValue(false);
    }
  }, [
    selectedStructureForPlanValue,
    slabRows,
    graceDays,
    effectiveFrom,
    SESSION_ID,
    SESSION_START_DATE,
    SESSION_END_DATE,
    fetchAllClassFineStructure,
  ]);

  const updatePlanValue = useCallback(async () => {
    if (!selectedStructureForPlanValue) return;
    if (!planValue.trim() || !graceDays.trim()) {
      myToast.notify_warning("Plan value and grace days are required");
      return;
    }
    const numericValue = parseFloat(planValue);
    const graceDaysValue = parseInt(graceDays, 10);
    // "No limit" toggle → send the explicit sentinel "No-Limit" that the
    // backend recognises (it maps to SQL NULL on tm_fine_rules.max_cap).
    let maxCapValue;
    if (noMaxCapLimit) {
      maxCapValue = "No-Limit";
    } else if (maxCap.trim() === "") {
      maxCapValue = "No-Limit";
    } else {
      maxCapValue = parseFloat(maxCap);
    }

    if (Number.isNaN(numericValue) || numericValue < 0) {
      myToast.notify_warning("Plan value must be a non-negative number");
      return;
    }
    if (Number.isNaN(graceDaysValue) || graceDaysValue < 0) {
      myToast.notify_warning("Grace days must be a non-negative number");
      return;
    }
    if (
      maxCapValue !== "No-Limit" &&
      (Number.isNaN(maxCapValue) || maxCapValue < 0)
    ) {
      myToast.notify_warning("Max cap must be a non-negative number");
      return;
    }

    // Effective From is optional. If provided, must be valid YYYY-MM-DD and
    // within the current session's date range (when known).
    const effectiveFromTrimmed = effectiveFrom.trim();
    if (effectiveFromTrimmed) {
      const isIso = /^\d{4}-\d{2}-\d{2}$/.test(effectiveFromTrimmed);
      const d = new Date(effectiveFromTrimmed);
      if (!isIso || Number.isNaN(d.getTime())) {
        myToast.notify_warning(
          "Effective From must be a valid date (YYYY-MM-DD)"
        );
        return;
      }
      if (SESSION_START_DATE && effectiveFromTrimmed < SESSION_START_DATE) {
        myToast.notify_warning(
          `Effective From cannot be before the session start (${SESSION_START_DATE})`
        );
        return;
      }
      if (SESSION_END_DATE && effectiveFromTrimmed > SESSION_END_DATE) {
        myToast.notify_warning(
          `Effective From cannot be after the session end (${SESSION_END_DATE})`
        );
        return;
      }
    }

    setIsEditingPlanValue(true);
    try {
      const schoolCode =
        SessionManager.getSchoolCode && SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateFineStructurePlanDetails.php`;
      const response = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: SESSION_ID,
          fine_rule_id: selectedStructureForPlanValue.fine_rule_id,
          fine_plan_value: numericValue,
          grace_days: graceDaysValue,
          max_cap: maxCapValue,
          // Empty string => leave existing value untouched on the backend.
          effective_from: effectiveFromTrimmed,
          class_id: selectedStructureForPlanValue.class_id,
          section_id: selectedStructureForPlanValue.section_id,
        },
      });
      if (response?.data?.api_status === "OK") {
        myToast.notify_success("Plan details updated");
        fetchAllClassFineStructure(true);
        setShowEditPlanValueDialog(false);
        setSelectedStructureForPlanValue(null);
        setPlanValue("");
        setGraceDays("");
        setMaxCap("");
        setNoMaxCapLimit(false);
        setEffectiveFrom("");
      } else {
        myToast.notify_warning(
          response?.data?.api_remark || "Failed to update plan details"
        );
      }
    } catch (e) {
      console.error("Error updating plan details:", e);
      myToast.notify_error("Error updating plan details");
    } finally {
      setIsEditingPlanValue(false);
    }
  }, [
    selectedStructureForPlanValue,
    planValue,
    graceDays,
    maxCap,
    noMaxCapLimit,
    effectiveFrom,
    SESSION_ID,
    SESSION_START_DATE,
    SESSION_END_DATE,
    fetchAllClassFineStructure,
  ]);

  /* ---------- Change plan dialog ---------- */
  const openChangePlanDialog = useCallback(
    async (structure) => {
      setSelectedStructureForChangePlan(structure);
      setNewFineRuleId("");
      setShowChangePlanDialog(true);

      setIsLoadingFineRules(true);
      try {
        const schoolCode =
          SessionManager.getSchoolCode && SessionManager.getSchoolCode();
        const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getFineRules.php`;
        const { data: response } = await axios.get(URL, {
          params: { school_code: schoolCode, session_id: SESSION_ID },
        });
        if (response?.api_status === "OK" && Array.isArray(response?.data)) {
          setAvailableFineRules(response.data);
        } else {
          setAvailableFineRules([]);
          myToast.notify_warning(
            response?.api_remark || "Failed to load fine plans"
          );
        }
      } catch (e) {
        console.error("Error loading fine rules:", e);
        setAvailableFineRules([]);
        myToast.notify_error("Error loading fine plans");
      } finally {
        setIsLoadingFineRules(false);
      }
    },
    [SESSION_ID]
  );

  const closeChangePlanDialog = useCallback(() => {
    if (isChangingPlan) return;
    setShowChangePlanDialog(false);
    setSelectedStructureForChangePlan(null);
    setNewFineRuleId("");
  }, [isChangingPlan]);

  const changePlan = useCallback(async () => {
    if (!selectedStructureForChangePlan) return;
    if (!newFineRuleId) {
      myToast.notify_warning("Please select a new fine plan");
      return;
    }
    if (newFineRuleId === selectedStructureForChangePlan.fine_rule_id) {
      myToast.notify_warning("Selected plan is the same as the current plan");
      return;
    }

    setIsChangingPlan(true);
    try {
      const schoolCode =
        SessionManager.getSchoolCode && SessionManager.getSchoolCode();
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateFineStructureRule.php`;
      const response = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          session_id: SESSION_ID,
          fine_rule_id: selectedStructureForChangePlan.fine_rule_id,
          new_fine_rule_id: newFineRuleId,
          class_id: selectedStructureForChangePlan.class_id,
          section_id: selectedStructureForChangePlan.section_id,
        },
      });
      if (response?.data?.api_status === "OK") {
        myToast.notify_success(
          response?.data?.api_remark || "Fine plan changed"
        );
        fetchAllClassFineStructure(true);
        setShowChangePlanDialog(false);
        setSelectedStructureForChangePlan(null);
        setNewFineRuleId("");
      } else {
        myToast.notify_warning(
          response?.data?.api_remark || "Failed to change fine plan"
        );
      }
    } catch (e) {
      console.error("Error changing fine plan:", e);
      myToast.notify_error("Error changing fine plan");
    } finally {
      setIsChangingPlan(false);
    }
  }, [
    selectedStructureForChangePlan,
    newFineRuleId,
    SESSION_ID,
    fetchAllClassFineStructure,
  ]);

  /* ---------- Derived ---------- */
  const filteredStructures = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return fineStructures;
    return fineStructures.filter((s) => {
      const hay = [
        s.class_name,
        s.section_name,
        s.fine_rule_name,
        s.rule_type,
      ]
        .map((v) => (v ?? "").toString().toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [fineStructures, searchTerm]);

  const isFiltered = Boolean(searchTerm.trim());
  const stableHandlers = useMemo(
    () => ({
      onToggle: toggleStructureStatus,
      onEditFeeDays: openEditFeeDaysDialog,
      onEditPlan: openEditPlanValueDialog,
      onChangePlan: openChangePlanDialog,
    }),
    [
      toggleStructureStatus,
      openEditFeeDaysDialog,
      openEditPlanValueDialog,
      openChangePlanDialog,
    ]
  );

  /* ---------- Render ---------- */
  return (
    <div
      className="content fine-page"
      style={{
        background: ui.pageBg,
        fontFamily: fontStack,
        color: ui.text,
        minHeight: "100%",
      }}
    >
      <style>{PAGE_CSS}</style>

      {/* Breadcrumbs */}
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ borderBottom: `1px solid ${ui.divider}`, paddingBottom: 6 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <a
            href="/AdminMain"
            aria-label="Admin home"
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(3, 169, 244, 0.12)",
              color: COLORS.primary_color_shade6,
              textDecoration: "none",
            }}
          >
            <FaHome />
          </a>
          <span
            className="d-inline-flex align-items-center gap-1"
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: ui.textSoft,
            }}
          >
            <FaSlidersH style={{ fontSize: 11 }} />
            Fine Settings
          </span>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div
        className="bg_page_white_rounded box_shadow animated fadeInUp"
        style={{
          "--mMargin": "16px",
          background: "transparent",
          boxShadow: "none",
        }}
      >
        {/* Hero card */}
        <div
          style={{
            background: ui.surface,
            borderRadius: ui.radiusXl,
            border: `1px solid ${ui.border}`,
            boxShadow: ui.shadowLift,
            overflow: "hidden",
            maxWidth: 1180,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Hero header */}
          <div
            style={{
              background: ui.heroGrad(COLORS),
              padding: "16px 18px",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {HERO_ORB_TR}
            {HERO_ORB_BL}

            <div
              className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center justify-content-between gap-3"
              style={{ position: "relative" }}
            >
              <div className="d-flex align-items-center gap-3 text-white flex-shrink-0 min-w-0">
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(6px)",
                  }}
                  aria-hidden
                >
                  <FaSlidersH style={{ fontSize: 16 }} />
                </span>
                <div className="min-w-0">
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 17,
                      letterSpacing: "0.01em",
                    }}
                  >
                    Fine Settings
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                    Configure class-wise fine structures, fee days & plan values
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={ChangeSession}
                  className="fp-pill"
                  title="Change session"
                  aria-label="Change session"
                  style={{
                    minWidth: 360,
                    maxWidth: "100%",
                    justifyContent: "space-between",
                    fontSize: 14,
                    padding: "8px 16px",
                    gap: 10,
                  }}
                >
                  <span
                    className="d-inline-flex align-items-center gap-2"
                    style={{ minWidth: 0 }}
                  >
                    <FaCalendarAlt aria-hidden />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {SESSION_NAME || "Select session"}
                    </span>
                  </span>
                  <FaExchangeAlt
                    style={{ fontSize: 12, opacity: 0.9, flexShrink: 0 }}
                    aria-hidden
                  />
                </button>
                <button
                  type="button"
                  onClick={() => fetchAllClassFineStructure(false)}
                  disabled={isLoadingStructures || !SESSION_ID}
                  className={`fp-icon-btn ${
                    isLoadingStructures ? "spinning" : ""
                  }`}
                  title="Refresh"
                  aria-label="Refresh"
                >
                  <FaSync aria-hidden />
                </button>
                <button
                  type="button"
                  onClick={() => updateAllFineStructureStatus(1)}
                  disabled={
                    !SESSION_ID ||
                    isLoadingStructures ||
                    fineStructures.length === 0
                  }
                  className="fp-pill"
                  title="Activate all"
                >
                  <FaCheckCircle aria-hidden /> Activate all
                </button>
                <button
                  type="button"
                  onClick={() => updateAllFineStructureStatus(0)}
                  disabled={
                    !SESSION_ID ||
                    isLoadingStructures ||
                    fineStructures.length === 0
                  }
                  className="fp-pill"
                  title="Deactivate all"
                >
                  <FaTimesCircle aria-hidden /> Deactivate all
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedStructureForFeeDays(null);
                    setFeeDayStart("");
                    setFeeDayEnd("");
                    setShowEditFeeDaysDialog(true);
                  }}
                  disabled={
                    !SESSION_ID ||
                    isLoadingStructures ||
                    fineStructures.length === 0
                  }
                  className="fp-pill"
                  title="Bulk edit fee days"
                >
                  <FaCalendarAlt aria-hidden /> Bulk fee days
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* List card */}
        <div
          style={{
            background: ui.surface,
            border: `1px solid ${ui.border}`,
            borderRadius: ui.radiusLg,
            boxShadow: ui.shadowSoft,
            overflow: "hidden",
            maxWidth: 1180,
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          {/* List header */}
          <div
            style={{
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade8} 0%, ${COLORS.primary_color_shade6} 100%)`,
              color: "#fff",
              padding: "14px 18px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {LIST_HEADER_ORB}
            <div
              className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-2"
              style={{ position: "relative" }}
            >
              <div className="d-flex align-items-center gap-3 min-w-0">
                <span
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(255,255,255,0.14)",
                    border: "1px solid rgba(255,255,255,0.28)",
                    backdropFilter: "blur(6px)",
                    flexShrink: 0,
                  }}
                  aria-hidden
                >
                  <FaTable style={{ fontSize: 15 }} />
                </span>
                <div className="min-w-0">
                  <div style={{ fontWeight: 800, fontSize: 15 }}>
                    Fine Structure (All Classes)
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      opacity: 0.85,
                      marginTop: 1,
                      fontWeight: 600,
                    }}
                  >
                    {isLoadingStructures
                      ? "Loading…"
                      : isFiltered
                        ? `${filteredStructures.length} of ${fineStructures.length} shown`
                        : `${fineStructures.length} total`}
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                <div className="fp-search-wrap">
                  <FaSearch className="fp-search-icon" aria-hidden />
                  <input
                    type="text"
                    className="fp-search"
                    placeholder="Search class, section, rule…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-label="Search fine structures"
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      className="fp-search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                      title="Clear"
                    >
                      <FaTimes />
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* List body */}
          <div className="p-3">
            {!SESSION_ID ? (
              <div
                className="text-center py-5"
                style={{ color: ui.muted, fontSize: 14 }}
              >
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                  aria-hidden
                >
                  <FaCalendarAlt style={{ fontSize: 22 }} />
                </div>
                <div
                  style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}
                >
                  Select a session
                </div>
                <div style={{ fontSize: 13 }}>
                  Pick a session above to view fine structures.
                </div>
                <button
                  type="button"
                  onClick={ChangeSession}
                  className="btn lift fp-cta mt-2"
                  style={{
                    borderRadius: 999,
                    fontWeight: 700,
                    padding: "8px 16px",
                    fontSize: 13,
                  }}
                >
                  <FaCalendarAlt className="me-1" /> Choose session
                </button>
              </div>
            ) : isLoadingStructures ? (
              <div className="fp-cards-grid">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : fineStructures.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: ui.muted, fontSize: 14 }}
              >
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                  aria-hidden
                >
                  <FaTable style={{ fontSize: 22 }} />
                </div>
                <div
                  style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}
                >
                  No fine structures found
                </div>
                <div style={{ fontSize: 13 }}>
                  No fine structures have been configured for this session yet.
                </div>
              </div>
            ) : filteredStructures.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: ui.muted, fontSize: 14 }}
              >
                <div
                  className="mx-auto mb-2 d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 18,
                    background: "rgba(148, 163, 184, 0.18)",
                    color: ui.muted,
                  }}
                  aria-hidden
                >
                  <FaSearch style={{ fontSize: 20 }} />
                </div>
                <div
                  style={{ fontWeight: 800, color: ui.text, fontSize: 15 }}
                >
                  No matching results
                </div>
                <div style={{ fontSize: 13 }}>Try a different search term.</div>
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="fp-btn-ghost mt-2"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="fp-cards-grid">
                {filteredStructures.map((structure) => (
                  <FineStructureCard
                    key={
                      structure.id ||
                      `${structure.class_id}-${structure.section_id}-${structure.fine_rule_id}`
                    }
                    structure={structure}
                    onToggle={stableHandlers.onToggle}
                    onEditFeeDays={stableHandlers.onEditFeeDays}
                    onEditPlan={stableHandlers.onEditPlan}
                    onChangePlan={stableHandlers.onChangePlan}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Session selector modal (lazy) */}
      {isOpenSessionDialog ? (
        <Suspense fallback={null}>
          <MdlSelectSession
            action={HandleCloseSessionDialog}
            action2={HandleSelectedSession}
          />
        </Suspense>
      ) : null}

      {/* Fee Days dialog */}
      {showEditFeeDaysDialog ? (
        <DialogShell
          title={selectedStructureForFeeDays ? "Edit Fee Days" : "Bulk Edit Fee Days"}
          subtitle={
            selectedStructureForFeeDays
              ? `${
                  selectedStructureForFeeDays.class_name ||
                  selectedStructureForFeeDays.class_id
                }${
                  selectedStructureForFeeDays.section_name &&
                  selectedStructureForFeeDays.section_name !== "No Section"
                    ? ` · ${selectedStructureForFeeDays.section_name}`
                    : ""
                }`
              : "Applies to all classes & sections"
          }
          onClose={closeEditFeeDaysDialog}
          footer={
            <>
              <button
                type="button"
                className="fp-btn-ghost"
                onClick={closeEditFeeDaysDialog}
                disabled={isEditingFeeDays}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn lift fp-cta"
                onClick={
                  selectedStructureForFeeDays ? updateFeeDays : updateAllFeeDays
                }
                disabled={
                  isEditingFeeDays ||
                  String(feeDayStart).trim() === "" ||
                  String(feeDayEnd).trim() === ""
                }
                style={{
                  borderRadius: 999,
                  fontWeight: 700,
                  padding: "7px 16px",
                  fontSize: 13,
                }}
              >
                {isEditingFeeDays
                  ? "Saving…"
                  : selectedStructureForFeeDays
                    ? "Update fee days"
                    : "Update all"}
              </button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-sm-6">
              <label className="fp-modal-label">Fee day start</label>
              <input
                type="number"
                className="form-control"
                value={feeDayStart}
                onChange={(e) => setFeeDayStart(e.target.value)}
                placeholder="e.g. 1"
                min="1"
                max="31"
                step="1"
              />
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: ui.muted,
                  fontWeight: 600,
                }}
              >
                Day of month (1–31)
              </div>
            </div>
            <div className="col-sm-6">
              <label className="fp-modal-label">Fee day end</label>
              <input
                type="number"
                className="form-control"
                value={feeDayEnd}
                onChange={(e) => setFeeDayEnd(e.target.value)}
                placeholder="e.g. 10"
                min={
                  String(feeDayStart).trim() !== "" &&
                  !Number.isNaN(parseInt(feeDayStart, 10))
                    ? Math.max(1, parseInt(feeDayStart, 10))
                    : 1
                }
                max="31"
                step="1"
              />
              <div
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: ui.muted,
                  fontWeight: 600,
                }}
              >
                Must be ≥ fee day start and ≤ 31
              </div>
            </div>
            <div className="col-12">
              <div
                className="d-flex align-items-start gap-2"
                style={{
                  background: ui.surfaceAlt,
                  border: `1px solid ${ui.divider}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  color: ui.textSoft,
                  fontSize: 12,
                }}
              >
                <FaInfoCircle
                  style={{ color: ui.info, marginTop: 2 }}
                  aria-hidden
                />
                <div>
                  Fines are calculated for the days between{" "}
                  <strong>start</strong> and <strong>end</strong> (inclusive).
                  Use the bulk option to apply the same range across every
                  class.
                </div>
              </div>
            </div>
          </div>
        </DialogShell>
      ) : null}

      {/* Plan Value dialog */}
      {showEditPlanValueDialog && selectedStructureForPlanValue ? (
        <DialogShell
          title={isSlabRule ? "Edit Slab Plan" : "Edit Plan Details"}
          subtitle={`${
            selectedStructureForPlanValue.class_name ||
            selectedStructureForPlanValue.class_id
          }${
            selectedStructureForPlanValue.section_name &&
            selectedStructureForPlanValue.section_name !== "No Section"
              ? ` · ${selectedStructureForPlanValue.section_name}`
              : ""
          }${isSlabRule ? " · Slab rule" : ""}`}
          onClose={closeEditPlanValueDialog}
          maxWidth={isSlabRule ? 620 : undefined}
          footer={
            <>
              <button
                type="button"
                className="fp-btn-ghost"
                onClick={closeEditPlanValueDialog}
                disabled={isEditingPlanValue}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn lift fp-cta"
                onClick={isSlabRule ? updateSlabPlan : updatePlanValue}
                disabled={
                  isEditingPlanValue ||
                  !graceDays.trim() ||
                  (isSlabRule
                    ? isLoadingSlabs || slabRows.length === 0
                    : !planValue.trim())
                }
                style={{
                  borderRadius: 999,
                  fontWeight: 700,
                  padding: "7px 16px",
                  fontSize: 13,
                }}
              >
                {isEditingPlanValue
                  ? "Saving…"
                  : isSlabRule
                    ? "Update slabs"
                    : "Update plan"}
              </button>
            </>
          }
        >
          <div className="row g-2">
            {isSlabRule ? (
              <div className="col-12">
                <label className="fp-modal-label">
                  <FaRupeeSign aria-hidden /> Fine slabs (overdue days → amount)
                </label>
                {isLoadingSlabs ? (
                  <div
                    className="fp-skeleton"
                    style={{ height: 90, borderRadius: 12 }}
                  />
                ) : (
                  <div className="fp-slab-grid">
                    <span className="fp-slab-col-label" aria-hidden />
                    <span className="fp-slab-col-label">From day</span>
                    <span className="fp-slab-col-label">To day (∞)</span>
                    <span className="fp-slab-col-label">Type</span>
                    <span className="fp-slab-col-label">Amount ₹</span>
                    <span className="fp-slab-col-label" aria-hidden />

                    {slabRows.map((row, index) => (
                      <React.Fragment key={index}>
                        <span className="fp-slab-index" aria-hidden>
                          {index + 1}
                        </span>
                        <input
                          type="number"
                          className="form-control"
                          value={row.from_day}
                          onChange={(e) =>
                            updateSlabRow(index, "from_day", e.target.value)
                          }
                          placeholder="1"
                          min="1"
                          aria-label={`Slab ${index + 1} from day`}
                        />
                        <input
                          type="number"
                          className="form-control"
                          value={row.to_day}
                          onChange={(e) =>
                            updateSlabRow(index, "to_day", e.target.value)
                          }
                          placeholder="∞"
                          min={row.from_day || 1}
                          title='Leave empty for "and above" (no upper limit)'
                          aria-label={`Slab ${index + 1} to day (empty for no upper limit)`}
                        />
                        <select
                          className="form-select"
                          value={row.calculation_type}
                          onChange={(e) =>
                            updateSlabRow(
                              index,
                              "calculation_type",
                              e.target.value
                            )
                          }
                          aria-label={`Slab ${index + 1} calculation type`}
                        >
                          {SLAB_CALC_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          className="form-control"
                          value={row.amount}
                          onChange={(e) =>
                            updateSlabRow(index, "amount", e.target.value)
                          }
                          placeholder="0"
                          min="0"
                          step="0.01"
                          aria-label={`Slab ${index + 1} amount`}
                        />
                        <button
                          type="button"
                          className="fp-slab-remove"
                          onClick={() => removeSlabRow(index)}
                          disabled={slabRows.length <= 1}
                          title="Remove slab"
                          aria-label={`Remove slab ${index + 1}`}
                        >
                          <FaTimes />
                        </button>
                      </React.Fragment>
                    ))}

                    <button
                      type="button"
                      className="fp-btn-ghost fp-slab-add"
                      onClick={addSlabRow}
                    >
                      + Add slab
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="col-12 col-md-6">
                <label className="fp-modal-label">
                  <FaRupeeSign aria-hidden /> Plan value
                </label>
                <input
                  type="number"
                  className="form-control"
                  value={planValue}
                  onChange={(e) => setPlanValue(e.target.value)}
                  placeholder="e.g. 100"
                  min="0"
                  step="0.01"
                />
              </div>
            )}
            <div className="col-12 col-md-6">
              <label className="fp-modal-label">Grace days</label>
              <input
                type="number"
                className="form-control"
                value={graceDays}
                onChange={(e) => setGraceDays(e.target.value)}
                placeholder="e.g. 5"
                min="0"
              />
            </div>
            {isSlabRule ? null : (
            <div className="col-12 col-md-6">
              <div className="d-flex align-items-center justify-content-between">
                <label
                  className="fp-modal-label mb-0"
                  htmlFor="fp-max-cap-input"
                >
                  Max cap
                </label>
                <label
                  className="d-inline-flex align-items-center gap-1"
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: ui.textSoft,
                    cursor: "pointer",
                    userSelect: "none",
                    marginBottom: 4,
                  }}
                  title="Toggle unlimited (no upper bound)"
                >
                  <input
                    type="checkbox"
                    checked={noMaxCapLimit}
                    onChange={(e) => {
                      const next = e.target.checked;
                      setNoMaxCapLimit(next);
                      if (next) setMaxCap("");
                    }}
                    style={{ accentColor: COLORS.primary_color_shade6 }}
                  />
                  No limit
                </label>
              </div>
              <input
                id="fp-max-cap-input"
                type="number"
                className="form-control"
                value={noMaxCapLimit ? "" : maxCap}
                onChange={(e) => setMaxCap(e.target.value)}
                placeholder={
                  noMaxCapLimit ? "No limit" : "Leave empty for no limit"
                }
                min="0"
                step="0.01"
                disabled={noMaxCapLimit}
                style={
                  noMaxCapLimit
                    ? { background: ui.surfaceAlt, color: ui.muted }
                    : undefined
                }
              />
            </div>
            )}
            <div className="col-12 col-md-6">
              <label
                className="fp-modal-label"
                htmlFor="fp-effective-from-input"
              >
                <FaCalendarAlt aria-hidden /> Effective From
              </label>
              <input
                id="fp-effective-from-input"
                type="date"
                className="form-control"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
                placeholder="YYYY-MM-DD"
                min={SESSION_START_DATE || undefined}
                max={SESSION_END_DATE || undefined}
              />
              {SESSION_START_DATE && SESSION_END_DATE ? (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    color: ui.muted,
                    fontWeight: 600,
                  }}
                >
                  Allowed range: {SESSION_START_DATE} → {SESSION_END_DATE}
                </div>
              ) : null}
            </div>
            <div className="col-12">
              <div
                className="d-flex align-items-start gap-2"
                style={{
                  background: ui.surfaceAlt,
                  border: `1px solid ${ui.divider}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  color: ui.textSoft,
                  fontSize: 12,
                }}
              >
                <FaInfoCircle
                  style={{ color: ui.info, marginTop: 2 }}
                  aria-hidden
                />
                <div>
                  {isSlabRule ? (
                    <>
                      Each slab maps a range of overdue days to an amount.
                      <strong> Fixed</strong> charges the amount once,
                      <strong> Per Day</strong> multiplies it by days late, and
                      <strong> Per Month</strong> multiplies it per 30-day
                      block. Leave "To day" empty on the last slab for "and
                      above". Ranges must not overlap. Effective From is
                      optional — leave blank to keep the existing date.
                    </>
                  ) : (
                    <>
                      Plan value and grace days are required. Leave max cap
                      empty (or toggle <strong>No limit</strong>) for no upper
                      bound. Effective From is optional — leave blank to keep
                      the existing date. Updates the fine rule (grace days, max
                      cap) and structure (plan value, effective from) together.
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </DialogShell>
      ) : null}

      {/* Change Plan dialog */}
      {showChangePlanDialog && selectedStructureForChangePlan ? (
        <DialogShell
          title="Change Fine Plan"
          subtitle={`${
            selectedStructureForChangePlan.class_name ||
            selectedStructureForChangePlan.class_id
          }${
            selectedStructureForChangePlan.section_name &&
            selectedStructureForChangePlan.section_name !== "No Section"
              ? ` · ${selectedStructureForChangePlan.section_name}`
              : ""
          }`}
          onClose={closeChangePlanDialog}
          footer={
            <>
              <button
                type="button"
                className="fp-btn-ghost"
                onClick={closeChangePlanDialog}
                disabled={isChangingPlan}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn lift fp-cta"
                onClick={changePlan}
                disabled={
                  isChangingPlan ||
                  !newFineRuleId ||
                  newFineRuleId === selectedStructureForChangePlan.fine_rule_id
                }
                style={{
                  borderRadius: 999,
                  fontWeight: 700,
                  padding: "7px 16px",
                  fontSize: 13,
                }}
              >
                {isChangingPlan ? "Changing…" : "Change plan"}
              </button>
            </>
          }
        >
          <div className="row g-3">
            <div className="col-12">
              <label className="fp-modal-label">Current plan</label>
              <div
                style={{
                  background: ui.surfaceAlt,
                  border: `1px solid ${ui.divider}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 13,
                  fontWeight: 700,
                  color: ui.text,
                }}
              >
                {selectedStructureForChangePlan.fine_rule_name || "—"}
                {selectedStructureForChangePlan.rule_type ? (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 11,
                      fontWeight: 700,
                      color: ui.muted,
                      textTransform: "capitalize",
                    }}
                  >
                    ({selectedStructureForChangePlan.rule_type})
                  </span>
                ) : null}
              </div>
            </div>
            <div className="col-12">
              <label className="fp-modal-label">
                <FaExchangeAlt aria-hidden /> New plan
              </label>
              {isLoadingFineRules ? (
                <div
                  className="fp-skeleton"
                  style={{ height: 40, borderRadius: 10 }}
                />
              ) : (
                <select
                  className="form-select"
                  value={newFineRuleId}
                  onChange={(e) => setNewFineRuleId(e.target.value)}
                  style={{ width: "100%" }}
                >
                  <option value="">Select a plan…</option>
                  {availableFineRules
                    .filter(
                      (rule) =>
                        rule.fine_rule_id !==
                        selectedStructureForChangePlan.fine_rule_id
                    )
                    .map((rule) => (
                      <option key={rule.fine_rule_id} value={rule.fine_rule_id}>
                        {rule.name || rule.fine_rule_id}
                        {rule.rule_type ? ` — ${rule.rule_type}` : ""}
                      </option>
                    ))}
                </select>
              )}
              {!isLoadingFineRules &&
              availableFineRules.filter(
                (rule) =>
                  rule.fine_rule_id !==
                  selectedStructureForChangePlan.fine_rule_id
              ).length === 0 ? (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: ui.warn,
                    fontWeight: 600,
                  }}
                >
                  No other fine plans available. Create one in Fine Rule
                  settings first.
                </div>
              ) : null}
            </div>
            <div className="col-12">
              <div
                className="d-flex align-items-start gap-2"
                style={{
                  background: ui.surfaceAlt,
                  border: `1px solid ${ui.divider}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  color: ui.textSoft,
                  fontSize: 12,
                }}
              >
                <FaInfoCircle
                  style={{ color: ui.info, marginTop: 2 }}
                  aria-hidden
                />
                <div>
                  Switches this class &amp; section to a different fine plan.
                  Grace days, max cap and rule type will follow the new plan.
                </div>
              </div>
            </div>
          </div>
        </DialogShell>
      ) : null}
    </div>
  );
};

export default FineSettings;
